import { z } from "zod";

import { assertNever } from "./utils.ts";

/** CRDT-grade string edit: at `pos`, delete `del` chars, insert `ins`. */
// Non-negative: a negative `pos` would mean "from the end" to String#slice but
// not to the conflict resolution, so the same op could land in two places.
// Observable insert()/remove() clamp before recording, so they never emit one.
export const StringOp = z.object({
  pos: z.int().nonnegative(),
  del: z.int().nonnegative(),
  ins: z.string(),
});
export type StringOp = z.infer<typeof StringOp>;

/** Splice-equivalent array edit (push/pop/shift/unshift/splice map to one). */
// Non-negative for the same reason as StringOp: Array#splice counts a
// negative `start` from the end, the conflict resolution does not. Observable
// splice() normalizes before recording.
export const ArrayOp = z.object({
  start: z.int().nonnegative(),
  deleteCount: z.int().nonnegative(),
  items: z.array(z.unknown()).optional(),
});
export type ArrayOp = z.infer<typeof ArrayOp>;

/**
 * One semantic graph operation on a normalized entity map. `deleted` holds
 * every removed entity's previous value (the whole cascade) — enough to
 * invert the op; `unlinked` says which ids were removed from which
 * surviving entities' children arrays.
 */
export const GraphDeleteOp = z.object({
  // TODO : remove op
  op: z.literal("deleteDeep"),
  /** The id the cascade started from. */
  id: z.string(),
  deleted: z.record(z.string(), z.unknown()),
  unlinked: z.record(z.string(), z.array(z.string())).optional(),
});
export type GraphDeleteOp = z.infer<typeof GraphDeleteOp>;

export const GraphOp = GraphDeleteOp;
export type GraphOp = z.infer<typeof GraphOp>;

const ChangePath = z.array(z.string());

// Change types for Undo/Redo history

export const UndoableChangeCreate = z.object({
  path: ChangePath,
  type: z.literal("create"),
  newValue: z.unknown(),
});
export type UndoableChangeCreate = z.infer<typeof UndoableChangeCreate>;

export const UndoableChangeDelete = z.object({
  path: ChangePath,
  type: z.literal("delete"),
  oldValue: z.unknown(),
});
export type UndoableChangeDelete = z.infer<typeof UndoableChangeDelete>;

export const UndoableChangeUpdate = z.object({
  path: ChangePath,
  type: z.literal("update"),
  newValue: z.unknown(),
  oldValue: z.unknown(),
});
export type UndoableChangeUpdate = z.infer<typeof UndoableChangeUpdate>;

export const UndoableChangeArraySplice = ArrayOp.extend({
  path: ChangePath,
  type: z.literal("array-splice"),
  oldValue: z.array(z.unknown()),
});
export type UndoableChangeArraySplice = z.infer<
  typeof UndoableChangeArraySplice
>;

export const UndoableChangeStringSplice = StringOp.extend({
  path: ChangePath,
  type: z.literal("string-splice"),
  oldValue: z.string(),
});
export type UndoableChangeStringSplice = z.infer<
  typeof UndoableChangeStringSplice
>;

export type UndoableChange =
  | UndoableChangeCreate
  | UndoableChangeDelete
  | UndoableChangeUpdate
  | UndoableChangeArraySplice
  | UndoableChangeStringSplice;

// type UndoableChangeDeepDelete = {
//   type: "deep-delete";
//   // TODO : what's the type?
//   newValue: unknown;
// } & GraphDeleteOp;

// Change types for Undo/Redo history before we assign path to them

export type RecordableChange =
  | Omit<UndoableChangeCreate, "path">
  | Omit<UndoableChangeDelete, "path">
  | Omit<UndoableChangeUpdate, "path">
  | Omit<UndoableChangeArraySplice, "path">
  | Omit<UndoableChangeStringSplice, "path">;

// Change types accepted in applyChangesToObservable

export type ApplicableChange =
  | UndoableChangeCreate
  | Omit<UndoableChangeDelete, "oldValue">
  | UndoableChangeUpdate
  | UndoableChangeArraySplice
  | UndoableChangeStringSplice;

// Change types for sending it over network and dealing with resolving conflicts

const ResolvableChangeArrayPush = z.object({
  path: ChangePath,
  type: z.literal("array-push"),
  value: z.unknown(),
});

const ResolvableChangeArrayFilterout = z.object({
  path: ChangePath,
  type: z.literal("array-filterout"),
  value: z.unknown(),
});

export const ResolvableChange = z.discriminatedUnion("type", [
  UndoableChangeCreate,
  UndoableChangeDelete.omit({ oldValue: true }),
  UndoableChangeUpdate,
  UndoableChangeArraySplice,
  UndoableChangeStringSplice,
  ResolvableChangeArrayPush,
  ResolvableChangeArrayFilterout,
]);
export type ResolvableChange = z.infer<typeof ResolvableChange>;

export function getOldValue(change: UndoableChange): unknown {
  switch (change.type) {
    case "create":
      return undefined;
    case "update":
    case "delete":
    case "array-splice":
    case "string-splice":
      // case "deep-delete":
      return change.oldValue;
    default:
      assertNever(change);
  }
}

export function getNewValue(change: UndoableChangeArraySplice): unknown[];
export function getNewValue(change: UndoableChangeStringSplice): string;
export function getNewValue(change: ApplicableChange): unknown;
export function getNewValue(change: ApplicableChange): unknown {
  switch (change.type) {
    case "create":
    case "update":
      // case "deep-delete":
      return change.newValue;
    case "delete":
      return undefined;
    case "array-splice": {
      const next = change.oldValue.slice();
      next.splice(change.start, change.deleteCount, ...(change.items ?? []));
      return next;
    }
    case "string-splice":
      return (
        change.oldValue.slice(0, change.pos) +
        change.ins +
        change.oldValue.slice(change.pos + change.del)
      );

    default:
      assertNever(change);
  }
}

// Removes any redundant properties from an UndoableChange to produce a minimal ResolvableChange
export function undoableToResolvableChange(
  change: UndoableChange,
): ResolvableChange {
  switch (change.type) {
    case "create":
      return {
        type: "create",
        path: change.path,
        newValue: change.newValue,
      };
    case "update":
      return {
        type: "update",
        path: change.path,
        oldValue: change.oldValue,
        newValue: change.newValue,
      };
    case "delete":
      return {
        type: "delete",
        path: change.path,
      };
    case "array-splice":
      return {
        type: "array-splice",
        path: change.path,
        oldValue: change.oldValue,
        start: change.start,
        deleteCount: change.deleteCount,
        items: change.items,
      };
    case "string-splice":
      return {
        type: "string-splice",
        path: change.path,
        oldValue: change.oldValue,
        pos: change.pos,
        del: change.del,
        ins: change.ins,
      };
    default:
      assertNever(change);
  }
}
